import React, {
  FormEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Anchor, FlatBox, PrimaryButton, H3, Label } from '@zooplus/ui-components';
import { useTranslation } from 'react-i18next';
import { FormFields } from './fields/FormFields';
import FirstNameField from './fields/FirstNameField';
import LastNameField from './fields/LastNameField';
import EmailField from './fields/EmailField';
import ReasonField from './fields/ReasonField';
import CustomerNumberField from './fields/CustomerNumberField';
import OrderNumberField from './fields/OrderNumberField';
import DescriptionField from './fields/DescriptionField';
import AttachmentSection from './fields/AttachmentSection';
import FormSecurityToken from './fields/FormSecurityToken';
import { FieldValidation, FormFieldValidationContext } from './context/FormFieldValidationContext';
import { useCustomerData } from '../../hooks';
import { KeycloakContext } from '../context/KeycloakContext';
import { LoaderContext } from '../context/LoaderContext';
import { ContactFormConnector } from '../../services/connectors/ContactFormConnector';
import FormConfirmation from './FormConfirmation';
import { useFormFieldValidationContextConfigurer } from './context/hooks/formFieldValidationContextConfigurer';
import { ValidationResult } from '../../hooks/validation/validator/FormFieldValidator';
import { useCustomerIdAndOrderIdIntegrity } from '../../hooks/validation/customerIdAndOrderIdIntegrity';
import { AppShopContext } from '../context/AppShopContext';
import { FormConfirmationProps } from './fields/types';
import Captcha from './fields/Captcha';
import { useModalShownParentNotification } from '../../hooks/useModalShownParentNotification';
import OrderStatusDetails from './order-status/OrderStatusDetails';
import { OrderStatus } from './order-status/model/OrderStatus';
import { encodeFileInBase64 } from '../../services/fileService';
import { ExperimentationContext, UserTrackingEvents } from '../context/ExperimentationContext';
import SelfReturnReminder from './self-return-reminder/SelfReturnReminder';
import ContactReasons from '../../constants/ContactReasons';
import SelfCancellationReminder from './self-cancellation-reminder/SelfCancellationReminder';
import Loader from '../loader/Loader';
import { OrderCompositeField } from '../../hooks/validation/types';
import { useOrderQueries } from './order-selector/hooks/useOrderQueries';
import { OrderSellerData } from './order-selector/model/Order';

const DEFAULT_COUNTRY = 'gb';
const DEFAULT_LANGUAGE = 'en';
const EXPERIMENTATION_REQUEST_DELAY = 1000;

const extractCountryCodeAndLanguageFromLocale = (locale?: string) => {
  let splitLocale = locale?.split('_') || '';
  if (splitLocale.length === 2) {
    return {
      language: splitLocale[0].toLowerCase(),
      country: splitLocale[1].toLowerCase(),
    };
  } else {
    return {
      language: DEFAULT_LANGUAGE,
      country: DEFAULT_COUNTRY,
    };
  }
};

const defaultFormConfirmationModalOptions: FormConfirmationProps = {
  isShown: false,
  onClose: () => {},
  onConfirm: () => {},
};

const Form: React.FC = () => {
  const appShopContext = useContext(AppShopContext);
  const keycloakContext = useContext(KeycloakContext);
  const experimentation = useContext(ExperimentationContext);
  const customerData = useCustomerData(keycloakContext);
  const loaderContext = useContext(LoaderContext);
  const { t } = useTranslation();
  const { getOrderSellerData } = useOrderQueries();
  const formElement = useRef<HTMLFormElement>(null);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(true);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [modalOptions, setModalOptions] = useState<FormConfirmationProps>(
    defaultFormConfirmationModalOptions,
  );
  const [parcelStatusLoading, setParcelStatusLoading] = useState(false);
  const languageCode = useMemo(
    () => extractCountryCodeAndLanguageFromLocale(appShopContext.locale).language || '',
    [appShopContext],
  );

  const [customerId, setCustomerId] = useState('');
  const [compositeOrder, setCompositeOrder] = useState<OrderCompositeField>({
    orderId: '',
    suborderId: '',
  });
  const [reason, setReason] = useState('');

  const [orderIdValidatedAndValid, setOrderIdValidatedAndValid] = useState(false);

  const [formFieldValidations, formFieldValidationsChange] =
    useFormFieldValidationContextConfigurer();

  // Experiment states
  const [asyncExperimentLoading, setAsyncExperimentLoading] = useState<boolean>(false);

  const [cancellationReminderEnabled, setCancellationReminderEnabled] = useState<boolean>(false);
  const [selfCancellationReminderWasDisplayed, setSelfCancellationReminderWasDisplayed] =
    useState<boolean>(false);
  const [isOrderCancellable, setIsOrderCancellable] = useState<boolean>(false);
  const [orderSellerData, setOrderSellerData] = useState<OrderSellerData>();
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null);
  const [orderStatusEnabled, setOrderStatusEnabled] = useState<boolean>(false);
  const [orderStatusWasDisplayed, setOrderStatusWasDisplayed] = useState(false);

  const [returnReminderEnabled, setReturnReminderEnabled] = useState<boolean>(false);
  const [selfReturnReminderWasDisplayed, setSelfReturnReminderWasDisplayed] =
    useState<boolean>(false);

  const displayOrderStatusDetails =
    orderIdValidatedAndValid &&
    orderStatusEnabled &&
    !orderStatusWasDisplayed &&
    orderSellerData?.is1POrder;
  const displaySelfReturnReminder =
    orderIdValidatedAndValid &&
    returnReminderEnabled &&
    !selfReturnReminderWasDisplayed &&
    orderSellerData?.is1POrder;
  const displaySelfCancellationReminder =
    orderIdValidatedAndValid &&
    cancellationReminderEnabled &&
    !selfCancellationReminderWasDisplayed &&
    orderSellerData?.is1POrder;

  useEffect(() => {
    if (!keycloakContext.tokenData?.token || !compositeOrder.orderId || !orderIdValidatedAndValid) {
      return;
    }
    getOrderSellerData(compositeOrder.orderId, compositeOrder.suborderId).then((it) =>
      setOrderSellerData(it),
    );
  }, [compositeOrder, orderIdValidatedAndValid, getOrderSellerData]);

  useEffect(() => {
    if (!keycloakContext.tokenData?.token || !compositeOrder.suborderId || !reason) {
      return;
    }

    setReturnReminderEnabled(false);
    setCancellationReminderEnabled(false);
    setOrderStatusEnabled(false);

    if (
      reason === ContactReasons.Return &&
      customerData &&
      experimentation.isSelfReturnReminderDisplayed()
    ) {
      setReturnReminderEnabled(true);
      return;
    }

    if (reason === ContactReasons.Cancellation) {
      setCancellationReminderEnabled(true);
      return;
    }

    let delayedCheck: ReturnType<typeof setTimeout> | undefined = undefined;

    if (reason === ContactReasons.Delivery || reason === ContactReasons.Order) {
      setAsyncExperimentLoading(true);
      delayedCheck = setTimeout(() => {
        experimentation
          .isOrderStatusDetailsExperimentEnabled()
          .then(setOrderStatusEnabled)
          .finally(() => setAsyncExperimentLoading(false));
      }, EXPERIMENTATION_REQUEST_DELAY);
    }
    return () => {
      clearTimeout(delayedCheck);
      setAsyncExperimentLoading(false);
    };
  }, [compositeOrder.suborderId, reason, customerData]);

  useEffect(() => {
    if (
      !orderIdValidatedAndValid ||
      !cancellationReminderEnabled ||
      !keycloakContext.tokenData?.token ||
      !compositeOrder.suborderId ||
      !orderSellerData?.is1POrder
    ) {
      return;
    }
    new ContactFormConnector().getIsOrderCancellable(compositeOrder.suborderId).then((it) => {
      setIsOrderCancellable(it.isOrderCancellable);
    });
  }, [
    orderIdValidatedAndValid,
    cancellationReminderEnabled,
    keycloakContext,
    compositeOrder.suborderId,
    orderSellerData,
  ]);

  useEffect(() => {
    if (!orderStatusEnabled || !orderIdValidatedAndValid || !orderSellerData?.is1POrder) {
      return;
    }

    setParcelStatusLoading(true);
    setOrderStatusWasDisplayed(false);

    const delayedAPICall = setTimeout(() => {
      new ContactFormConnector()
        .getOrderStatus(compositeOrder.suborderId)
        .then((statusData) => {
          const data = statusData.toData();
          setOrderStatus(data.status ? new OrderStatus(data) : null);
          setOrderStatusWasDisplayed(!data.status);
          if (data.status) {
            experimentation.trackClick(
              UserTrackingEvents.CF_ORDER_AND_PARCEL_DELIVERY_STATUS_DISPLAYED,
            );
          }
        })
        .catch(() => setOrderStatusWasDisplayed(true))
        .finally(() => setParcelStatusLoading(false));
    }, EXPERIMENTATION_REQUEST_DELAY);
    return () => {
      clearTimeout(delayedAPICall);
    };
  }, [
    orderIdValidatedAndValid,
    orderStatusEnabled,
    compositeOrder.suborderId,
    experimentation,
    orderSellerData,
  ]);

  const onFormSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async function (evt: React.FormEvent) {
      evt.preventDefault();

      const validationResultsMap = await validateForm();

      if (!allFormFieldsValid(validationResultsMap)) {
        focusFirstInvalidField(validationResultsMap);
        return;
      }

      setSubmitButtonEnabled(false);
      loaderContext.requestLoaderShow('form-submit');
      experimentation.trackMetric(UserTrackingEvents.FAQ_CONTACT_US_FORM_SUBMIT, {
        contactReason: reason,
      });

      if (orderStatus) {
        experimentation.trackClick(
          UserTrackingEvents.CF_SUBMITTED_ORDER_AND_PARCEL_DELIVERY_STATUS_DISPLAYED,
        );
      }

      // @ts-ignore
      const formDataManager = new FormDataManager(formElement.current!!);

      const closeModal = () => {
        setModalOptions({ ...defaultFormConfirmationModalOptions, isShown: false });
      };
      const reloadPage = () => {
        window.location.reload();
      };

      const onSuccess = () => {
        loaderContext.requestLoaderHide('form-submit');
        setModalOptions({
          type: 'success',
          isShown: true,
          onClose: reloadPage,
          onConfirm: reloadPage,
        });
      };
      const onFailure = () => {
        loaderContext.requestLoaderHide('form-submit');
        setModalOptions({
          type: 'failure',
          isShown: true,
          onClose: closeModal,
          onConfirm: closeModal,
        });
        setSubmitButtonEnabled(true);
      };

      await formDataManager.convertFilesToBase64();
      formDataManager.submit().then(onSuccess).catch(onFailure);
    },
    [formFieldValidations, experimentation, reason],
  );

  const validateForm = async () => {
    const validationResults: { [key: string]: ValidationResult } = {};
    const validationPromises = Object.keys(formFieldValidations).map((fieldName) => {
      return formFieldValidations[fieldName]
        .validate()
        .then((validationResult: ValidationResult) => {
          validationResults[fieldName] = validationResult;
        });
    });
    await Promise.all(validationPromises);
    return validationResults;
  };
  const allFormFieldsValid = (validationResultsMap: any) =>
    Object.values(validationResultsMap).every(
      (validationResult) => (validationResult as ValidationResult).isValid,
    );
  const getFirstInvalidFieldName = (validationResultsMap: any) =>
    Object.keys(FormFields) // get ordered field names from the enum
      .find((fieldName) => !(validationResultsMap[fieldName] as FieldValidation).isValid);
  const focusFirstInvalidField = (validationResultsMap: any) => {
    const firstInvalidFieldName = getFirstInvalidFieldName(validationResultsMap);
    Object.keys(formFieldValidations).forEach((fieldName: string) => {
      formFieldValidationsChange({
        action: 'setFocus',
        fieldName,
        value: fieldName === firstInvalidFieldName,
      });
    });
  };

  const dismissSelfCancellationReminder = useCallback(
    () => setSelfCancellationReminderWasDisplayed(true),
    [],
  );

  const { orderNumberValidator, isProcessingOrderNumber } = useCustomerIdAndOrderIdIntegrity(
    formFieldValidations,
    customerId,
    compositeOrder,
  );

  const gdprCustomerInfo = () => {
    let translation = t('form.sections.customer_data.privacy_policy_url_header');
    return (
      translation && (
        <div>
          {translation}
          <Anchor
            isBold
            target="_blank"
            variant="primary"
            className="pp-px-2xs"
            href={t(`common.my_account_gdpr_url`)}>
            {t('form.sections.customer_data.privacy_policy_url_text')}
          </Anchor>
        </div>
      )
    );
  };

  const onCaptchaVerified = useCallback(() => {
    setCaptchaVerified(true);
  }, []);

  const onCaptchaEnabled = useCallback(() => {
    setCaptchaEnabled(true);
  }, []);

  useModalShownParentNotification(modalOptions.isShown, appShopContext.domain);

  return (
    <div>
      <FlatBox variant={'white'} rounded className="contactForm__container">
        <form
          name="contactForm"
          className="contactForm__form"
          action="#"
          encType="multipart/form-data"
          noValidate={false}
          onSubmit={onFormSubmit}
          ref={formElement}>
          <H3>{t('form.sections.customer_data.header')}</H3>
          <FormFieldValidationContext.Provider value={formFieldValidations[FormFields.firstName]}>
            <FirstNameField externalValue={customerData?.firstName} />
          </FormFieldValidationContext.Provider>

          <FormFieldValidationContext.Provider value={formFieldValidations[FormFields.lastName]}>
            <LastNameField externalValue={customerData?.lastName} />
          </FormFieldValidationContext.Provider>

          <FormFieldValidationContext.Provider value={formFieldValidations[FormFields.email]}>
            <EmailField externalValue={customerData?.email} />
          </FormFieldValidationContext.Provider>

          <div>
            <div>
              <Label isRequired /> {t('form.sections.customer_data.required_fields')}
            </div>
            {gdprCustomerInfo()}
          </div>

          <hr className="contactForm__divider" />

          <H3>{t('form.sections.contact_data.header')}</H3>

          <FormFieldValidationContext.Provider value={formFieldValidations[FormFields.reason]}>
            <ReasonField onChange={setReason} />
          </FormFieldValidationContext.Provider>

          <FormFieldValidationContext.Provider
            value={formFieldValidations[FormFields.customerNumber]}>
            <CustomerNumberField
              externalValue={customerData?.customerId}
              onChange={setCustomerId}
            />
          </FormFieldValidationContext.Provider>

          <FormFieldValidationContext.Provider value={formFieldValidations[FormFields.orderNumber]}>
            <OrderNumberField
              onChange={setCompositeOrder}
              setOrderIdValidatedAndValid={setOrderIdValidatedAndValid}
              isProcessing={isProcessingOrderNumber}
              externalValidator={orderNumberValidator}
            />
          </FormFieldValidationContext.Provider>
          <Loader isLoading={asyncExperimentLoading} variant="zooplus">
            {displayOrderStatusDetails && (
              <OrderStatusDetails
                setOrderStatusDetailsWasDisplayed={setOrderStatusWasDisplayed}
                orderStatus={orderStatus!!}
                parcelStatusLoading={parcelStatusLoading}
              />
            )}
            {displaySelfReturnReminder && (
              <SelfReturnReminder
                orderId={compositeOrder.suborderId}
                onDismiss={() => setSelfReturnReminderWasDisplayed(true)}
              />
            )}
            {displaySelfCancellationReminder && (
              <SelfCancellationReminder
                orderId={compositeOrder.suborderId}
                isOrderCancellable={isOrderCancellable}
                onDismiss={dismissSelfCancellationReminder}
              />
            )}
            {!(
              displayOrderStatusDetails ||
              displaySelfReturnReminder ||
              displaySelfCancellationReminder
            ) && (
              <>
                <FormFieldValidationContext.Provider
                  value={formFieldValidations[FormFields.description]}>
                  <DescriptionField />
                </FormFieldValidationContext.Provider>

                <FormFieldValidationContext.Provider
                  value={formFieldValidations[FormFields.attachment]}>
                  <AttachmentSection />
                </FormFieldValidationContext.Provider>
              </>
            )}
          </Loader>

          <div className="captcha-container">
            <Captcha
              domain={appShopContext.domain}
              language={languageCode}
              onVerified={onCaptchaVerified}
              onEnabled={onCaptchaEnabled}
            />
          </div>
          <FormSecurityToken />
          <input
            id="site-id"
            name="siteId"
            type="text"
            hidden
            defaultValue={appShopContext.siteId}
          />
          <PrimaryButton
            className="submit-button"
            id="submit"
            type="submit"
            data-test="contactForm.field.submitButton"
            disabled={
              !submitButtonEnabled ||
              displaySelfReturnReminder ||
              displaySelfCancellationReminder ||
              displayOrderStatusDetails ||
              isProcessingOrderNumber ||
              asyncExperimentLoading ||
              (captchaEnabled && !captchaVerified)
            }>
            {t('form.submit')}
          </PrimaryButton>
        </form>
      </FlatBox>

      <FormConfirmation
        type={modalOptions.type}
        onClose={modalOptions.onClose}
        onConfirm={modalOptions.onConfirm}
        isShown={modalOptions.isShown!}
      />
    </div>
  );
};
export default Form;

class FormDataManager {
  private readonly form: HTMLFormElement;
  private readonly formData: FormData;

  constructor(formElement: HTMLFormElement) {
    this.form = formElement;
    this.formData = new FormData(formElement);
  }

  submit(): Promise<any> {
    return new ContactFormConnector().submitContactFormV2(this.formData);
  }

  async convertFilesToBase64(): Promise<void> {
    const fileInputs = Array.from(
      this.form.querySelectorAll<HTMLInputElement>('input[name="attachments"]'),
    );
    const files = fileInputs.flatMap((input) => Array.from(input.files || []));
    this.formData.delete('attachments');
    if (!files.length) return;
    const attachmentData = await Promise.all(files.map(encodeFileInBase64));
    attachmentData.forEach((attachment) => this.formData.append(`attachments`, attachment));
  }

  reset(): void {
    this.form.reset();
  }
}
